<script module lang="ts">
  import MarketingShell from "@/layouts/marketing-shell.svelte"

  // Same public marketing chrome (header, footer, glow) as the TOBY homepage.
  export const layout = MarketingShell
</script>

<script lang="ts">
  import type { FormComponentSlotProps } from "@inertiajs/core"
  import { Form } from "@inertiajs/svelte"
  import { LoaderCircle } from "@lucide/svelte"
  import CircleCheckIcon from "@lucide/svelte/icons/circle-check"
  import { _, locale } from "svelte-i18n"

  import InputError from "@/components/input-error.svelte"
  import MarketingSeo from "@/components/marketing-seo.svelte"
  import TurnstileWidget from "@/components/turnstile-widget.svelte"
  import { Button } from "@/components/ui/button"
  import { Input } from "@/components/ui/input"
  import { Label } from "@/components/ui/label"
  import { Textarea } from "@/components/ui/textarea"
  import { BRAND_NAME } from "@/lib/brand"
  import { baseLanguage } from "@/lib/locale-routes"

  // The confirmation of receipt for a declaration just filed — §312k(3)
  // wants the customer able to keep their declaration with its date and
  // time, so it is on screen as well as in the email.
  // No termination date and no account detail by design: anyone can submit
  // this form with anyone's address and land here. The dates go to the
  // account holder's inbox (CancellationMailer#account_receipt).
  type Receipt = {
    received_at: string
    status: string
    contact_email: string
  }

  let {
    turnstile_site_key,
    receipt,
  }: {
    turnstile_site_key: string | null
    receipt: Receipt | null
  } = $props()

  // The base language the POST carries — the server validates the field
  // against the plain language list (CancellationsController#form_locale),
  // so a geo-overlay locale like "de-CH" still files as "de".
  const lang = $derived(baseLanguage($locale ?? "en"))

  // §312k(2) no. 1: ordinary or for cause, and a reason is only meaningful
  // (and only required) for the latter.
  let kind = $state("ordinary")
  // §312k(2) no. 4: the customer states WHEN it should end. "Earliest
  // possible" is the default because it is what almost everyone wants; the
  // date input only appears for someone who needs a specific one.
  let endTiming = $state("earliest")

  const dateTime = (iso: string) =>
    new Intl.DateTimeFormat($locale ?? "en", {
      dateStyle: "long",
      timeStyle: "short",
    }).format(new Date(iso))

  const radioRowClass =
    "flex items-start gap-3 rounded-lg border border-input p-3 text-sm"
</script>

<MarketingSeo
  title={`${$_("cancel.title")} — ${BRAND_NAME}`}
  description={$_("cancel.subtitle")}
/>

<main class="mx-auto w-full max-w-xl px-6 pt-28 pb-36 sm:pt-40">
  {#if receipt}
    <div class="flex flex-col items-center gap-4 text-center">
      <CircleCheckIcon class="size-12 text-green-600" />
      <h1 class="text-3xl font-medium tracking-tight">
        {$_("cancel.success_title")}
      </h1>

      <p class="text-[#706f6c] dark:text-[#A1A09A]">
        {$_("cancel.success_received", {
          values: { datetime: dateTime(receipt.received_at) },
        })}
      </p>

      {#if receipt.status === "scheduled"}
        <p class="text-lg font-medium">
          {$_("cancel.success_check_email")}
        </p>
        <p class="text-[#706f6c] dark:text-[#A1A09A]">
          {$_("cancel.success_check_email_hint")}
        </p>
      {:else if receipt.status === "no_contract"}
        <p class="text-[#706f6c] dark:text-[#A1A09A]">
          {$_("cancel.success_no_contract")}
        </p>
      {:else}
        <p class="text-[#706f6c] dark:text-[#A1A09A]">
          {$_("cancel.success_under_review")}
        </p>
      {/if}

      <p class="text-sm text-[#706f6c] dark:text-[#A1A09A]">
        {$_("cancel.success_email_note", {
          values: { email: receipt.contact_email },
        })}
      </p>
    </div>
  {:else}
    <div class="mb-10 text-center">
      <h1 class="mb-3 text-4xl font-medium tracking-tight sm:text-5xl">
        {$_("cancel.title")}
      </h1>
      <p class="text-lg text-[#706f6c] dark:text-[#A1A09A]">
        {$_("cancel.subtitle")}
      </p>
    </div>

    <Form method="post" action="/cancel" class="flex flex-col gap-5">
      {#snippet children({ processing, errors }: FormComponentSlotProps)}
        <input type="hidden" name="locale" value={lang} />

        <fieldset class="flex flex-col gap-2">
          <legend class="mb-2 text-sm font-medium">
            {$_("cancel.kind_label")}
          </legend>
          <label class={radioRowClass}>
            <input
              type="radio"
              name="kind"
              value="ordinary"
              bind:group={kind}
              class="accent-primary mt-0.5 size-4 shrink-0"
            />
            <span>
              <span class="block font-medium">{$_("cancel.kind_ordinary")}</span
              >
              <span class="text-[#706f6c] dark:text-[#A1A09A]">
                {$_("cancel.kind_ordinary_hint")}
              </span>
            </span>
          </label>
          <label class={radioRowClass}>
            <input
              type="radio"
              name="kind"
              value="extraordinary"
              bind:group={kind}
              class="accent-primary mt-0.5 size-4 shrink-0"
            />
            <span>
              <span class="block font-medium">
                {$_("cancel.kind_extraordinary")}
              </span>
              <span class="text-[#706f6c] dark:text-[#A1A09A]">
                {$_("cancel.kind_extraordinary_hint")}
              </span>
            </span>
          </label>
          <InputError messages={errors.kind} />
        </fieldset>

        {#if kind === "extraordinary"}
          <div class="flex flex-col gap-2">
            <Label for="reason">{$_("cancel.reason_label")}</Label>
            <Textarea
              id="reason"
              name="reason"
              required
              maxlength={5000}
              placeholder={$_("cancel.reason_placeholder")}
              class="min-h-32"
            />
            <InputError messages={errors.reason} />
          </div>
        {/if}

        <div class="grid gap-5 sm:grid-cols-2">
          <div class="flex flex-col gap-2">
            <Label for="declarant_name">{$_("cancel.name_label")}</Label>
            <Input
              id="declarant_name"
              name="declarant_name"
              required
              maxlength={200}
            />
            <InputError messages={errors.declarant_name} />
          </div>
          <div class="flex flex-col gap-2">
            <Label for="contact_email">{$_("cancel.email_label")}</Label>
            <Input
              id="contact_email"
              name="contact_email"
              type="email"
              required
              maxlength={320}
            />
            <InputError messages={errors.contact_email} />
          </div>
        </div>
        <p class="-mt-3 text-xs text-[#706f6c] dark:text-[#A1A09A]">
          {$_("cancel.email_note")}
        </p>

        <div class="flex flex-col gap-2">
          <Label for="account_email">{$_("cancel.account_email_label")}</Label>
          <Input
            id="account_email"
            name="account_email"
            type="email"
            maxlength={320}
          />
          <p class="text-xs text-[#706f6c] dark:text-[#A1A09A]">
            {$_("cancel.account_email_note")}
          </p>
          <InputError messages={errors.account_email} />
        </div>

        <div class="flex flex-col gap-2">
          <Label for="customer_reference">{$_("cancel.reference_label")}</Label>
          <Input
            id="customer_reference"
            name="customer_reference"
            maxlength={100}
            placeholder={$_("cancel.reference_placeholder")}
          />
          <p class="text-xs text-[#706f6c] dark:text-[#A1A09A]">
            {$_("cancel.reference_note")}
          </p>
          <InputError messages={errors.customer_reference} />
        </div>

        <div class="flex flex-col gap-2">
          <Label for="contract_hint">{$_("cancel.contract_label")}</Label>
          <Input
            id="contract_hint"
            name="contract_hint"
            maxlength={500}
            placeholder={$_("cancel.contract_placeholder")}
          />
          <p class="text-xs text-[#706f6c] dark:text-[#A1A09A]">
            {$_("cancel.contract_note")}
          </p>
          <InputError messages={errors.contract_hint} />
        </div>

        <fieldset class="flex flex-col gap-2">
          <legend class="mb-2 text-sm font-medium"
            >{$_("cancel.end_label")}</legend
          >
          <label class={radioRowClass}>
            <input
              type="radio"
              name="end_timing"
              value="earliest"
              bind:group={endTiming}
              class="accent-primary mt-0.5 size-4 shrink-0"
            />
            <span>{$_("cancel.end_earliest")}</span>
          </label>
          <label class={radioRowClass}>
            <input
              type="radio"
              name="end_timing"
              value="custom"
              bind:group={endTiming}
              class="accent-primary mt-0.5 size-4 shrink-0"
            />
            <span>{$_("cancel.end_custom")}</span>
          </label>
          {#if endTiming === "custom"}
            <Input
              id="requested_end_on"
              name="requested_end_on"
              type="date"
              required
            />
          {/if}
          <p class="text-xs text-[#706f6c] dark:text-[#A1A09A]">
            {$_("cancel.end_note")}
          </p>
        </fieldset>

        {#if turnstile_site_key}
          <div class="flex flex-col gap-2">
            <TurnstileWidget siteKey={turnstile_site_key} />
            <InputError messages={errors.turnstile} />
          </div>
        {/if}

        <Button type="submit" disabled={processing} class="self-start">
          {#if processing}
            <LoaderCircle class="h-4 w-4 animate-spin" />
          {/if}
          {$_("cancel.submit")}
        </Button>

        <p class="text-xs text-[#706f6c] dark:text-[#A1A09A]">
          {$_("cancel.alt_channel")}
          <a href="mailto:support@tobysite.com" class="underline"
            >support@tobysite.com</a
          >
        </p>
      {/snippet}
    </Form>
  {/if}
</main>
